import { useQuery } from '@apollo/client'
import { useMemo } from 'react'

import { IPrepItemBatchStandardsType, getPrepItemBatchStandards } from '../gql'

export interface ICountTypeData {
  batchType: string
  batchTypeId?: number
  standardUnitsPerBatch: number
  unitId: number
  unit: string
}

export interface IPrepItemOnHandTableData {
  key: string
  prepItem: string
  prepItemId: string
  countTypes: ICountTypeData[]
  // For table display
  countType1: string
  countType1PerStandardUnit: string | number
  countType2: string
  countType2PerStandardUnit: string | number
  countType3: string
  countType3PerStandardUnit: string | number
}

const usePrepItemOnHandDataSource = () => {
  const { data, loading, refetch } = useQuery<IPrepItemBatchStandardsType>(
    getPrepItemBatchStandards,
  )

  const dataSource = useMemo(() => {
    if (!data?.getPrepItemBatchStandards) return []

    // Filter for ONHAND items
    const onHandItems = data.getPrepItemBatchStandards.nodes.filter(
      (item) => item.sourceType === 'ONHAND',
    )

    // Group by prepItemName to handle multiple count types
    const groupedItemsMap = new Map<string, IPrepItemOnHandTableData>()

    onHandItems.forEach((item) => {
      const existing = groupedItemsMap.get(item.prepItemIdCode)

      const countTypeData: ICountTypeData = {
        batchType: item.prepBatchType,
        batchTypeId: item.batchTypeId,
        standardUnitsPerBatch: item.standardUnitsPerBatch,
        unitId: item.unitId,
        unit: item.unit,
      }

      if (existing) {
        existing.countTypes.push(countTypeData)
      } else {
        groupedItemsMap.set(item.prepItemIdCode, {
          key: item.prepItemIdCode,
          prepItem: item.prepItemName,
          prepItemId: item.prepItemIdCode,
          countTypes: [countTypeData],
          countType1: '',
          countType1PerStandardUnit: '',
          countType2: '',
          countType2PerStandardUnit: '',
          countType3: '',
          countType3PerStandardUnit: '',
        })
      }
    })

    // Convert map to array and populate display fields
    const groupedItems = Array.from(groupedItemsMap.values()).map((item) => {
      const updated = { ...item }

      if (item.countTypes[0]) {
        updated.countType1 = item.countTypes[0].batchType
        updated.countType1PerStandardUnit =
          item.countTypes[0].standardUnitsPerBatch
      }
      if (item.countTypes[1]) {
        updated.countType2 = item.countTypes[1].batchType
        updated.countType2PerStandardUnit =
          item.countTypes[1].standardUnitsPerBatch
      }
      if (item.countTypes[2]) {
        updated.countType3 = item.countTypes[2].batchType
        updated.countType3PerStandardUnit =
          item.countTypes[2].standardUnitsPerBatch
      }

      return updated
    })

    return groupedItems
  }, [data])

  return {
    loading,
    dataSource,
    refetch,
  }
}

export default usePrepItemOnHandDataSource
